import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Bem-vindo de volta!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Ops, algo deu errado. Tente novamente.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden"
      data-testid="login-page"
    >
      {/* Ambient orb — mesmo padrao do jornada-virtu */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[-15%] left-1/2 -translate-x-1/2
          w-[480px] h-[480px] rounded-full opacity-[0.06]"
        style={{
          background: 'radial-gradient(circle, hsl(142 71% 45%) 0%, transparent 70%)',
          filter: 'blur(72px)',
        }}
      />

      <div className="w-full max-w-md animate-fade-up relative z-10">

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight"
            style={{ fontFamily: "'DM Serif Display', serif" }}>
            <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">
              VIRTU
            </span>
          </h1>
          <p className="text-muted-foreground mt-2 text-sm font-light">
            Sua jornada de transformação começa aqui
          </p>
        </div>

        {/* Card */}
        <Card className="bg-card/60 backdrop-blur-xl border border-border/30 shadow-2xl rounded-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Entrar na sua conta
            </CardTitle>
            <CardDescription className="text-muted-foreground text-sm">
              Acesse sua jornada de superação
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm text-foreground/80">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-xl bg-muted/50 border-border/40
                    focus:border-primary/50 focus:ring-primary/20
                    placeholder:text-muted-foreground/40"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-foreground/80">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPass ? 'text' : 'password'}
                    data-testid="login-password-input"
                    placeholder="Sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-xl pr-10 bg-muted/50 border-border/40
                      focus:border-primary/50 focus:ring-primary/20
                      placeholder:text-muted-foreground/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2
                      text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                    aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Lembrar-me alinhado à esquerda */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={remember}
                  onCheckedChange={setRemember}
                  data-testid="remember-me-checkbox"
                  className="border-border/50 data-[state=checked]:bg-primary
                    data-[state=checked]:border-primary"
                />
                <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer select-none">
                  Lembrar-me
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-full font-semibold text-sm
                  bg-primary text-primary-foreground
                  hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20
                  transition-all duration-300 active:scale-[0.97] touch-manipulation"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Carregando...' : 'Entrar'}
              </Button>

            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground/50 mt-6">
          &copy; 2026 VIRTU — Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
